import { useState, useEffect, useRef } from "react";
import { ArrowRight, Search, Briefcase } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useDebounce } from "../hooks/useDebounce";
import { SalaryData } from "../../../types/SalaryData";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { postJobtitles } from "../../../utils/api";
import { SalaryCheckRequestModel } from "../../../types/SalaryCheckRequest";
import Analytics from "../../../utils/analytics";
import { educationLevels, regions, ageRanges, sectors, responsibilities, situations } from "../data/form-options";

interface DesktopStepWizardProps {
  salaryData: SalaryData;
  onDataChange: (data: Partial<SalaryData>) => void;
  onCalculate: (data: SalaryCheckRequestModel) => void;
  contentSourceId: number;
  resetTrigger?: number;
}

export interface JobTitleItem {
  id: number;
  title: string;
}

const TOTAL_STEPS = 4;

export function DesktopStepWizard({ salaryData, onDataChange, onCalculate, contentSourceId, resetTrigger }: DesktopStepWizardProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState(0);
  const [jobTitleInput, setJobTitleInput] = useState(salaryData.jobTitle || "");
  const [showJobTitles, setShowJobTitles] = useState(false);
  const [jobTitleFocused, setJobTitleFocused] = useState(false);
  const [salaryFocused, setSalaryFocused] = useState(false);
  const [jobTitleResults, setJobTitleResults] = useState<JobTitleItem[]>([]);
  const [jobTitleError, setJobTitleError] = useState<string | null>(null);

  //Nyt
  const [educationId, setEducationId] = useState<number | null>(null);
  const [regionId, setRegionId] = useState<number | null>(null);
  const [responsebilityId, setResponsebilityId] = useState<number | null>(null);
  const [hasTrackedSituation, setHasTrackedSitation] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const MIN_SEARCH_LENGTH = 3;
  const SEARCH_DELAY = 300;

  const salaryResultRequest = () => {
    //When editing result educationId has to be found again
    const matchingEducationId = () => {
      if(educationId == null && salaryData.education != null && salaryData.education !== "") {
        return educationLevels.find(e => e.title === salaryData.education)?.id ?? 0
      } else {
        return educationId;
      }  
    } 

    const matchingSituationId = () => {
      return salaryData.situationId || (situations.find(e => e.title === salaryData.situation)?.id ?? 0);
    } 

    //Request data
    const requestData: SalaryCheckRequestModel = {
        name: "",
        email: "",
        contentSourceId: contentSourceId,
        situationId: salaryData.situationId || matchingSituationId(),
        jobTitleId: salaryData.jobTitleId === 0 ? (jobTitleResults.find(j => j.title === salaryData.jobTitle)?.id ?? 0) : salaryData.jobTitleId,
        jobTitle: salaryData.jobTitle,
        salary: salaryData.salary,
        educationId: matchingEducationId(),
        jobExperience: salaryData.experience,
        regionId: regionId,
        age: null,
        ageGroup: salaryData.age,
        jobSector: salaryData.sector,
        managementExperience: responsebilityId,
        cookie: "",
        educationLevel: salaryData.education,
    };
    onCalculate(requestData);
  }

  const searchJobTitles = async (query: string) => {
    try {
      const response = await postJobtitles({ query: query });
      const data = Array.isArray(response.data)
        ? response.data.map((item: any, idx: number) => ({
            id: item.id ?? idx,
            title: item.title ?? String(item),
          }))
        : [];
      setJobTitleResults(data);
      setShowJobTitles(true);
    } catch (err) {
      setJobTitleError("Kunne ikke hente jobtitler. Prøv igen.");
    }
  };

  const debouncedSearch = useDebounce((query: string) => {
    if (query.length >= MIN_SEARCH_LENGTH) {
      searchJobTitles(query);
    } else if (query.length === 0) {
      setJobTitleResults([]);
      setShowJobTitles(false);
    }
  }, SEARCH_DELAY);

  const handleJobTitleSelect = (title: string, id: number) => {
    setJobTitleInput(title);
    onDataChange({ jobTitle: title, jobTitleId: id });
    setShowJobTitles(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current && !inputRef.current.contains(event.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowJobTitles(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const canAdvance = () => {
    switch (currentStep) {
      case 1:
        return !!salaryData.situation;
      case 2:
        return !!salaryData.jobTitle;
      case 3:
        return true; // Optional fields
      case 4:
        return salaryData.salary > 0;
      default:
        return false;
    }
  };

  const handleNext = () => {
    track();

    if (!canAdvance()) return;

    if (currentStep === TOTAL_STEPS) {
       //onCalculate();
      salaryResultRequest();
    } else {
      setDirection(1);
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setDirection(-1);
      setCurrentStep(currentStep - 1);
    }
  };

  const progressPct = (currentStep / TOTAL_STEPS) * 100;

  //tracking
  const hasTracked = useRef(false);

  useEffect(() => {
    if(!hasTracked.current) {
      Analytics.getInstance().trackSalaryCheck("situation_shown_test")
      hasTracked.current = true;
    } 
  }, [])

  const track = () => {
    let situationMap =
    salaryData.situation === "Er min løn fair" ? "_fair_salary" :
    salaryData.situation === "Lønforhandling" ? "_negotiation" :
    salaryData.situation === "Nyuddannet" ? "_graduated" :
    salaryData.situation === "Søger nyt job" ? "_new_job" :
    "";

    let background = ""
    if(salaryData.experience) {
      background += `_${salaryData.experience}`
    }
    if(salaryData.education) {
      background += `_${salaryData.education}`
    }
    if(salaryData.age) {
      background += `_${salaryData.age}`
    }

    let resultContentType = "";
    if (salaryData.region) {
      resultContentType += `_${salaryData.region}`
    } 
    if(salaryData.sector) {
      resultContentType += `_${salaryData.sector}`
    }
    if(salaryData.experience) {
      resultContentType += `_${salaryData.experience}`
    }

    if (currentStep === 1) {
      Analytics.getInstance().trackSalaryCheck(`situation${situationMap}`)
    } else if (currentStep === 2) {
      Analytics.getInstance().trackSalaryCheck(`title_${salaryData.jobTitle}`)
    } else if (currentStep === 3) {
      Analytics.getInstance().trackSalaryCheck(`background${background}`)
    } else if (currentStep === 4) {
      const contentType = `result${resultContentType}`
      Analytics.getInstance().trackSalaryCheckWithValue(contentType, `${salaryData.salary}`)
    }
  }

  return (
    <div className="bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.04)] border border-gray-100 overflow-hidden">
      {/* Step indicator and progress */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-bold text-[#7b7b7a] uppercase tracking-wide">
            Trin {currentStep} af {TOTAL_STEPS}
          </span>
          <span className="text-xs font-medium text-[#7b7b7a]">
            {Math.round(progressPct)}%
          </span>
        </div>
        <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#D76F2C] rounded-full transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Step content */}
      <div className="px-6 py-6 min-h-[380px]">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStep}
            custom={direction}
            initial={{ x: direction * 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: direction * -40, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            {/* Step 1: Situation */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <div>
                  <h2 className="text-[22px] font-bold text-[#3A3A39] mb-2" style={{fontSize: "1.4rem"}}>
                    Hvad er din situation?
                  </h2>
                  <p className="text-sm text-[#7b7b7a]">
                    Fortæl os hvorfor du tjekker din løn, så kan vi give dig de mest relevante råd.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2">
                  {situations.map((s) => (
                    <button
                      key={s.title}
                      onClick={() => {onDataChange({ situation: s.title, situationId: s.id })}}
                      className={`text-center px-6 py-4 rounded-[20px!important] text-[15px] font-medium transition-all cursor-pointer ${
                        salaryData.situation === s.title
                          ? "bg-[#008E9F!important] text-[#fff!important]"
                          : "bg-[#E6F4F5!important] text-[#3A3A39!important] hover:bg-[#d1e8ea!important]"
                      }`}
                    >
                      {s.title}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Jobtitel */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <div>
                  <h2 className="text-[22px] font-bold text-[#3A3A39] mb-2" style={{fontSize: "1.4rem"}}>
                    Hvad er din jobtitel?
                  </h2>
                  <p className="text-sm text-[#7b7b7a]">
                    Vi finder løndata der matcher din stilling præcist.
                  </p>
                </div>
                <div className="pt-2">
                  <label className="text-[15px] font-medium text-[#3A3A39] mb-2 block">
                    Jobtitel *
                  </label>
                  <div className="relative">
                    <div className={`relative flex items-center rounded-lg transition-all ${
                      salaryData.jobTitle
                        ? "border-2 border-[#479376] bg-[#edf4f1]"
                        : "border border-gray-300 bg-white"
                    } focus-within:border-[#479376] focus-within:border-2`}>
                      <Search className="absolute left-4 w-4 h-4 text-gray-400 pointer-events-none" />
                      <input
                        type="text"
                        value={jobTitleInput}
                        onChange={(e) => { setJobTitleInput(e.target.value); debouncedSearch(e.target.value); }}
                        //onFocus={() => { setShowJobTitles(true); setJobTitleFocused(true); }}
                        onBlur={() => setJobTitleFocused(false)}
                        placeholder="Søg fx projektleder, konsulent…"
                        className="w-full pl-11 pr-4 py-3.5 bg-transparent rounded-lg focus:outline-none text-[15px] text-[#3A3A39] placeholder:text-gray-400"
                        ref={inputRef}
                      />
                    </div>
                    {showJobTitles && (
                      <div
                        className="absolute z-50 w-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-y-auto"
                        ref={dropdownRef}
                        onMouseDown={(e) => e.preventDefault()}
                      >

                        {jobTitleError && (
                          <div className="px-4 py-3 text-[15px] text-red-600">{jobTitleError}</div>
                        )}

                        {!jobTitleError && jobTitleResults.length === 0 && jobTitleInput.trim() !== "" && (
                          <div className="px-4 py-3 text-[15px] text-[#3A3A39]">Ingen jobtitler fundet.</div>
                        )}

                        {jobTitleResults.map((item, i) => (
                          <button
                            key={item.id}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleJobTitleSelect(item.title, item.id);
                            }}
                            className={`w-full text-left px-4 py-3 text-[15px] hover:bg-[#f0f5f5!important] transition-colors cursor-pointer flex items-center gap-3 ${
                              i === 0 ? "rounded-t-lg" : ""
                            } ${i === jobTitleResults.length - 1 ? "rounded-b-lg" : ""} ${
                              item.title === salaryData.jobTitle ? "bg-[#e6f4f5] text-[#3A3A39] font-medium" : "text-[#3A3A39]"
                            }`}
                            style={{ justifyContent: "flex-start" }}
                          >
                            <Briefcase className="w-4 h-4 text-gray-400 shrink-0" />
                            {item.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Baggrund */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <div>
                  <h2 className="text-[22px] font-bold text-[#3A3A39] mb-2" style={{fontSize: "1.4rem"}}>
                    Fortæl om din baggrund
                  </h2>
                  <p className="text-sm text-[#7b7b7a]">
                    Disse oplysninger gør vores vurdering mere præcis (valgfrit).
                  </p>
                </div>
                <div className="space-y-4 pt-2">
                  {/* Experience */}
                  <div>
                    <label className="text-[15px] font-medium text-[#3A3A39] mb-2 block">
                      Erfaring
                    </label>
                    <div className="flex items-center gap-3">
                      <div className={`relative flex items-center rounded-lg transition-all bg-white flex-1 ${
                        salaryData.experience !== null
                          ? "border-2 border-[#008E9F]"
                          : "border border-gray-300"
                      } focus-within:border-[#008E9F] focus-within:border-2`}>
                        <input
                          type="number"
                          min={0}
                          max={50}
                          value={salaryData.experience ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            onDataChange({ experience: val === "" ? null : Math.min(50, Math.max(0, Number(val))) });
                          }}
                          placeholder="fx 5"
                          className="w-full px-4 py-3.5 bg-transparent rounded-lg focus:outline-none text-[15px] text-[#3A3A39] placeholder:text-gray-400"
                        />
                      </div>
                      <span className="text-[15px] text-[#3A3A39] shrink-0 font-medium">år</span>
                    </div>
                  </div>

                  <FieldSelect
                    label="Uddannelse"
                    value={salaryData.education}
                    onChange={(v) => {onDataChange({ education: v }); setEducationId(educationLevels.find(e => e.title === v)?.id ?? 0)}}
                    options={educationLevels.map(e => e.title)}
                    placeholder="Vælg uddannelsesniveau"
                  />

                  <FieldSelect
                    label="Alder"
                    value={salaryData.age}
                    onChange={(v) => onDataChange({ age: v })}
                    options={ageRanges}
                    placeholder="Vælg aldersgruppe"
                  />
                </div>
              </div>
            )}

            {/* Step 4: Løn og kontekst */}
            {currentStep === 4 && (
              <div className="space-y-4">
                <div>
                  <h2 className="text-[22px] font-bold text-[#3A3A39] mb-2" style={{fontSize: "1.4rem"}}>
                    Løn og arbejdsforhold
                  </h2>
                  <p className="text-sm text-[#7b7b7a]">
                    Hjælp os med at finde den mest relevante sammenligning.
                  </p>
                </div>
                <div className="space-y-4 pt-2">
                  <FieldSelect
                    label="Region"
                    value={salaryData.region}
                    onChange={(v) => {onDataChange({ region: v }), setRegionId(regions.find(r => r.title === v)?.id ?? null)}}
                    options={regions.map(r => r.title)}
                    placeholder="Vælg region"
                  />

                  <FieldSelect
                    label="Sektor"
                    value={salaryData.sector}
                    onChange={(v) => onDataChange({ sector: v })}
                    options={sectors}
                    placeholder="Vælg sektor"
                  />

                  <FieldSelect
                    label="Ansvarsniveau"
                    value={salaryData.responsibility}
                    onChange={(v) => {onDataChange({ responsibility: v }); setResponsebilityId(responsibilities.findIndex(r => r === v) ?? null)}}
                    options={responsibilities}
                    placeholder="Vælg ansvarsniveau"
                  />

                  {/* Salary */}
                  <div>
                    <label className="text-[15px] font-medium text-[#3A3A39] mb-2 block">
                      Bruttoløn (kr./md.) *
                    </label>
                    <div className={`relative flex items-center rounded-lg transition-all ${
                      salaryData.salary > 0
                        ? "border-2 border-[#479376] bg-[#edf4f1]"
                        : "border border-gray-300 bg-white"
                    } focus-within:border-[#479376] focus-within:border-2`}>
                      <span className="absolute left-4 text-[15px] text-gray-400 font-medium pointer-events-none">kr.</span>
                      <input
                        type="number"
                        value={salaryData.salary || ""}
                        onChange={(e) => onDataChange({ salary: Number(e.target.value) })}
                        onFocus={() => setSalaryFocused(true)}
                        onBlur={() => setSalaryFocused(false)}
                        placeholder="fx 35.000"
                        className="w-full pl-11 pr-4 py-3.5 bg-transparent rounded-lg focus:outline-none text-[15px] text-[#3A3A39] placeholder:text-gray-400"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation */}
      <div className="px-6 py-5 border-t border-gray-100 bg-[#fafafa]">
        <div className={`flex gap-3 justify-around ${currentStep === 1 ? "justify-end" : "justify-between"}`}>
          {currentStep > 1 && (
            <button
              onClick={handleBack}
              className="text-[15px!important] font-medium text-[#008E9F!important] underline hover:text-[#006d7a!important] transition-colors cursor-pointer"
            >
              Tilbage
            </button>
          )}
          <button
            onClick={handleNext}
            disabled={!canAdvance()}
            className={`button ml-auto flex items-center justify-center gap-2 py-3.5 px-8 rounded-[40px] text-base font-bold text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-[0.98] shadow-sm cursor-pointer ${
              currentStep === TOTAL_STEPS
                ? "bg-[#D76F2C] hover:bg-[#c15f1f]"
                : "bg-[#D76F2C] hover:bg-[#c15f1f]"
            }`}
          >
            {currentStep === TOTAL_STEPS ? (
              <>
                Se mit resultat
              </>
            ) : (
              <>
                Næste
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      {/*   {currentStep === TOTAL_STEPS && (
          <p className="text-xs text-center text-[#7b7b7a] mt-3">
            Baseret på flere datakilder for et mere præcist resultat.
          </p>
        )} */}
      </div>
    </div>
  );
}

function FieldSelect({ label, value, onChange, options, placeholder }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder: string;
}) {
  return (
    <div>
      <label className="text-[15px] font-medium text-[#3A3A39] mb-2 block">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
        style={{
            width: "100%",
            padding: "0.3rem 1rem",
            borderRadius: "0.5rem",
            fontSize: "15px",
            transition: "all 0.2s ease",
            minHeight: "auto",
            borderStyle: "solid",
            borderWidth: value ? "2px" : "1px",
            borderColor: value ? "#479376" : "var(--color-gray-300)",
            color: value ? "#3A3A39" : "var(--color-gray-400)",
            backgroundColor: value ? "#edf4f1" : "#ffffff",
            fontWeight: value ? 500 : 400,
            boxSizing: "border-box",
            outline: "none",
            justifyContent: "space-between",
          }}
        >
          <SelectValue style={{textAlign: "left"}} placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          {options.map((opt) => (
            <SelectItem
              key={opt}
              value={opt}
              className="text-[15px] px-4 py-3 hover:bg-[#f0f5f5!important] cursor-pointer text-[#3A3A39] data-[state=checked]:bg-[#e6f4f5!important] data-[state=checked]:text-[#3A3A39] rounded"
            >
              {opt}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
